import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const FechamentoMensal = () => {
  const anoAtual = Number(format(new Date(), 'yyyy'));
  const [carregando, setCarregando] = useState(false);
  const [processandoMes, setProcessandoMes] = useState('');
  const [ano, setAno] = useState(anoAtual);
  const [dados, setDados] = useState({ meses: [], logs: [] });
  const [observacoes, setObservacoes] = useState({});

  const carregar = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/fechamento-mensal', { params: { ano } });
      const payload = response.data || {};
      setDados({
        meses: Array.isArray(payload.meses) ? payload.meses : [],
        logs: Array.isArray(payload.logs) ? payload.logs : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar fechamento mensal');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [ano]);

  const fechar = async (mes) => {
    const key = `${ano}-${mes}`;
    setProcessandoMes(key);
    try {
      await axios.post('/api/fechamento-mensal/fechar', {
        ano,
        mes,
        observacao: observacoes[key] || '',
      });
      toast.success(`Periodo ${String(mes).padStart(2, '0')}/${ano} fechado`);
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao fechar periodo');
    } finally {
      setProcessandoMes('');
    }
  };

  const reabrir = async (mes) => {
    const key = `${ano}-${mes}`;
    setProcessandoMes(key);
    try {
      await axios.post('/api/fechamento-mensal/reabrir', {
        ano,
        mes,
        observacao: observacoes[key] || '',
      });
      toast.success(`Periodo ${String(mes).padStart(2, '0')}/${ano} reaberto`);
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao reabrir periodo');
    } finally {
      setProcessandoMes('');
    }
  };

  const resumo = useMemo(() => {
    const fechados = (dados.meses || []).filter((item) => item.fechado).length;
    return {
      fechados,
      abertos: 12 - fechados,
    };
  }, [dados.meses]);

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fechamento Mensal</h1>
          <p className="text-gray-600">Trava de periodo e log de auditoria</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Ano</label>
              <input
                type="number"
                min="2000"
                max="2100"
                className="form-input"
                value={ano}
                onChange={(e) => setAno(Number(e.target.value || anoAtual))}
              />
            </div>
            <div className="flex items-end">
              <button className="btn-primary" onClick={carregar}>Atualizar</button>
            </div>
            <div className="card bg-gray-50 border">
              <div className="card-body py-3">
                <p className="text-xs text-gray-500">Meses Fechados</p>
                <p className="text-xl font-bold text-danger-700">{resumo.fechados}</p>
              </div>
            </div>
            <div className="card bg-gray-50 border">
              <div className="card-body py-3">
                <p className="text-xs text-gray-500">Meses Abertos</p>
                <p className="text-xl font-bold text-success-700">{resumo.abertos}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Status por Mes</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>Status</th>
                  <th>Fechado em</th>
                  <th>Por</th>
                  <th>Observacao</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {(dados.meses || []).map((item) => {
                  const mes = Number(item.mes);
                  const key = `${ano}-${mes}`;
                  const emProcesso = processandoMes === key;
                  return (
                    <tr key={key}>
                      <td>{MONTHS[mes - 1]} / {ano}</td>
                      <td>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${item.fechado ? 'bg-danger-100 text-danger-700' : 'bg-success-100 text-success-700'}`}>
                          {item.fechado ? 'Fechado' : 'Aberto'}
                        </span>
                      </td>
                      <td>{item.fechado_at ? format(new Date(item.fechado_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                      <td>{item.fechado_por_nome || '-'}</td>
                      <td className="min-w-[260px]">
                        <input
                          className="form-input"
                          placeholder="Motivo (opcional)"
                          value={observacoes[key] ?? ''}
                          onChange={(e) => setObservacoes((old) => ({ ...old, [key]: e.target.value }))}
                        />
                      </td>
                      <td>
                        {item.fechado ? (
                          <button className="btn-secondary" onClick={() => reabrir(mes)} disabled={emProcesso}>
                            {emProcesso ? 'Processando...' : 'Reabrir'}
                          </button>
                        ) : (
                          <button className="btn-primary" onClick={() => fechar(mes)} disabled={emProcesso}>
                            {emProcesso ? 'Processando...' : 'Fechar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Log de Auditoria</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Mes</th>
                  <th>Acao</th>
                  <th>Usuario</th>
                  <th>Observacao</th>
                </tr>
              </thead>
              <tbody>
                {(dados.logs || []).map((log) => (
                  <tr key={log.id}>
                    <td>{format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}</td>
                    <td>{String(log.mes).padStart(2, '0')}/{log.ano}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${log.acao === 'fechar' ? 'bg-danger-100 text-danger-700' : 'bg-success-100 text-success-700'}`}>
                        {log.acao}
                      </span>
                    </td>
                    <td>{log.usuario_nome || '-'}</td>
                    <td>{log.observacao || '-'}</td>
                  </tr>
                ))}
                {(dados.logs || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-6">Sem logs para este ano</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FechamentoMensal;
